/**
 * Turning a create-CV failure into something a user can act on.
 *
 * The flow used to render `error.message` verbatim, which is fine for the
 * skill service's own sentences ("This skill needs ...") and useless for the
 * two shapes that actually reached users: a raw ZodError array serialized by
 * `fail('VALIDATION_ERROR', error.message)`, and a bare HTTP code. Pure and
 * React-free so it can be tested without a renderer.
 */

/** Minimal shape of `SkillsApiError` - kept structural to stay dependency-free. */
function errorCode(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : '';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message.trim() : '';
}

/**
 * A serialized ZodError (or any other JSON blob) is noise, not a sentence: it
 * starts with a bracket and is never something to show someone.
 */
export function isMachineMessage(message: string): boolean {
  if (!message) return true;
  if (/^[[{]/.test(message)) return true;
  // `Error: HTTP 500` and friends carry no information the user can use.
  return /^(HTTP[_ ]?\d{3}|Request failed|Network request failed)\b/i.test(
    message,
  );
}

/** i18n key under `cv.create.errors.*` for a known failure, else null. */
export function createCvErrorKey(error: unknown): string | null {
  switch (errorCode(error)) {
    case 'SKILL_INPUT_REQUIRED':
      return 'skillInputsRequired';
    case 'CV_SOURCE_INCOMPLETE':
      return 'sourceCvEmpty';
    // The wire code, NOT the name of the constant that holds it - they differ
    // (`SKILL_ENTITLEMENT_DENIED = 'SUBSCRIPTION_ENTITLEMENT_DENIED'`), and
    // matching the name meant a Free user picking a paid skill fell through to
    // the generic copy. Spelled out rather than imported so this module stays
    // free of the analytics/native imports `skills-api` pulls in; the value is
    // pinned server-side in `skill-declared-inputs.spec.ts`.
    case 'SUBSCRIPTION_ENTITLEMENT_DENIED':
      return 'skillNotIncluded';
    case 'NETWORK_ERROR':
    case 'HTTP_408':
    case 'HTTP_502':
    case 'HTTP_503':
    case 'HTTP_504':
      return 'network';
    case 'HTTP_429':
      return 'tooManyRequests';
    default:
      return null;
  }
}

/**
 * The sentence to show. A known code wins (it is written for this screen), then
 * the server's own message when it reads as prose, then the generic fallback.
 */
export function resolveCreateCvErrorMessage(
  error: unknown,
  t: (key: string) => string,
): string {
  const key = createCvErrorKey(error);
  if (key) return t(`cv.create.errors.${key}`);
  const message = errorMessage(error);
  if (message && !isMachineMessage(message)) return message;
  return t('cv.create.errors.failedBody');
}
